import React, { useCallback, useEffect, useState } from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { CustomButton, CustomInput } from '../../components';
import FirebaseUtil from '../../config/firebase';
import firebase from 'firebase';
import { useSnackbar } from 'notistack';
import comonSnackbarOpts from '../../utilities/comonSnackBarOpt';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableHeader: {
      backgroundColor: theme.palette.primary.main,
    },
    tableHeaderText: {
      color: theme.palette.primary.contrastText,
    },
    sectionTitle: {
      marginTop: '30px',
    },
    buttonWrapper: {
      width: '200px',
      height: '55px',
    },
  }),
);

interface Banner {
  id: string;
  title: string;
  created_at: firebase.firestore.Timestamp | null;
  updated_at: firebase.firestore.Timestamp | null;
}

const Banners: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [banners, setBanners] = useState<Banner[]>([]);
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetchBanners = useCallback(async () => {
    const snapshot = await FirebaseUtil.firestore
      .collection('banners')
      .orderBy('created_at', 'desc')
      .get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Banner));
  }, []);

  useEffect(() => {
    (async () => {
      const data = await fetchBanners();
      setBanners(data);
    })();
  }, [fetchBanners]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      return enqueueSnackbar('Please enter a banner title', {
        variant: 'error',
        ...comonSnackbarOpts,
      });
    }

    setIsSubmitting(true);
    try {
      if (editId) {
        await FirebaseUtil.firestore.collection('banners').doc(editId).update({
          title: title.trim(),
          updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        });
        enqueueSnackbar('Banner updated successfully', {
          variant: 'success',
          ...comonSnackbarOpts,
        });
        setEditId(null);
      } else {
        const now = firebase.firestore.FieldValue.serverTimestamp();
        await FirebaseUtil.firestore.collection('banners').add({
          title: title.trim(),
          created_at: now,
          updated_at: now,
        });
        enqueueSnackbar('Banner added successfully', {
          variant: 'success',
          ...comonSnackbarOpts,
        });
      }
      setTitle('');
      const data = await fetchBanners();
      setBanners(data);
    } catch (error) {
      enqueueSnackbar(editId ? 'Failed to update banner' : 'Failed to add banner', {
        variant: 'error',
        ...comonSnackbarOpts,
      });
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickEdit = (banner: Banner) => {
    setEditId(banner.id);
    setTitle(banner.title);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setTitle('');
  };

  const handleOpenDelete = (id: string) => {
    setSelectedId(id);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedId(null);
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setIsDeleting(true);
    try {
      await FirebaseUtil.firestore.collection('banners').doc(selectedId).delete();
      enqueueSnackbar('Banner deleted', {
        variant: 'success',
        ...comonSnackbarOpts,
      });
      const data = await fetchBanners();
      setBanners(data);
      setOpenDelete(false);
      setSelectedId(null);
    } catch (error) {
      enqueueSnackbar('Failed to delete banner', {
        variant: 'error',
        ...comonSnackbarOpts,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (ts: firebase.firestore.Timestamp | null) => {
    if (!ts) return '—';
    return ts.toDate().toLocaleString();
  };

  

  return (
    <div>
      <Typography variant="h6" component="h3">
        {editId ? 'Edit Banner' : 'Add Banner'}
      </Typography>
      <br />
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={8}>
          <CustomInput
            fullWidth
            variant="filled"
            name="title"
            label="Banner Title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
            type="text"
          />
        </Grid>
        <Grid item xs={4} style={{ display: 'flex', gap: '12px' }}>
          <CustomButton
            className={classes.buttonWrapper}
            disabled={isSubmitting}
            loading={isSubmitting}
            size="small"
            variant="contained"
            onClick={handleSubmit}
            color="primary"
          >
            {editId ? 'Update' : 'Add Banner'}
          </CustomButton>
          {editId && (
            <Button
              variant="outlined"
              color="default"
              onClick={handleCancelEdit}
              disabled={isSubmitting}
              style={{ height: '55px' }}
            >
              Cancel
            </Button>
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" component="h3" className={classes.sectionTitle}>
        All Banners
      </Typography>
      <br />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableHeaderText} align="center">No.</TableCell>
              <TableCell className={classes.tableHeaderText} align="left">Title</TableCell>
              <TableCell className={classes.tableHeaderText} align="left">Created At</TableCell>
              <TableCell className={classes.tableHeaderText} align="left">Updated At</TableCell>
              <TableCell className={classes.tableHeaderText} align="left">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners.map((banner, index) => (
              <TableRow key={banner.id} selected={banner.id === editId}>
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell align="left">{banner.title}</TableCell>
                <TableCell align="left">{formatDate(banner.created_at)}</TableCell>
                <TableCell align="left">{formatDate(banner.updated_at)}</TableCell>
                <TableCell align="left">
                  <IconButton onClick={() => handleClickEdit(banner)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleOpenDelete(banner.id)}>
                    <DeleteIcon style={{ color: 'red' }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {banners.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">No banners yet</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDelete} onClose={handleCloseDelete}>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this banner?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CustomButton
            onClick={handleDelete}
            color="primary"
            loading={isDeleting}
            disabled={isDeleting}
          >
            Yes
          </CustomButton>
          <Button onClick={handleCloseDelete} color="primary" disabled={isDeleting}>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Banners;
